import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

interface GiftItem {
  id: string;
  name: string;
  description: string;
  points_required: number;
  stock: number;
  image_url: string | null;
  category: string;
  is_active: boolean;
  created_at: string;
}

interface GiftExchange {
  id: string;
  user_id: string;
  gift_item_id: string | null;
  gift_item_name: string;
  points_spent: number;
  status: string;
  gift_image_url: string | null;
  created_at: string;
  completed_at: string | null;
  user_nickname?: string;
  user_email?: string;
}

type TabMode = "items" | "exchanges";

export default function AdminGiftsPage() {
  const [tabMode, setTabMode] = useState<TabMode>("items");
  const [giftItems, setGiftItems] = useState<GiftItem[]>([]);
  const [exchanges, setExchanges] = useState<GiftExchange[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state for creating/editing gift item
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<GiftItem | null>(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formPoints, setFormPoints] = useState("");
  const [formStock, setFormStock] = useState("");
  const [formCategory, setFormCategory] = useState("일반");
  const [formImageFile, setFormImageFile] = useState<File | null>(null);
  const [formImagePreview, setFormImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Upload modal for exchange
  const [uploadModal, setUploadModal] = useState<GiftExchange | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // ---- Fetch data ----
  const fetchGiftItems = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("gift_items")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setGiftItems(data || []);
    } catch {
      setMessage({ type: "error", text: "기프티콘 상품 목록을 불러오지 못했습니다" });
    }
  }, []);

  const fetchExchanges = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("gift_exchanges")
        .select(`
          id, user_id, gift_item_id, gift_item_name, points_spent, status, gift_image_url, created_at, completed_at,
          profiles:user_id (nickname, email)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const mapped: GiftExchange[] = (data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        user_id: row.user_id as string,
        gift_item_id: row.gift_item_id as string | null,
        gift_item_name: row.gift_item_name as string,
        points_spent: row.points_spent as number,
        status: row.status as string,
        gift_image_url: row.gift_image_url as string | null,
        created_at: row.created_at as string,
        completed_at: row.completed_at as string | null,
        user_nickname: (row.profiles as Record<string, unknown>)?.nickname as string || "익명",
        user_email: (row.profiles as Record<string, unknown>)?.email as string || "",
      }));

      setExchanges(mapped);
    } catch {
      setMessage({ type: "error", text: "교환 요청 목록을 불러오지 못했습니다" });
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchGiftItems(), fetchExchanges()]);
      setLoading(false);
    };
    load();
  }, [fetchGiftItems, fetchExchanges]);

  // ---- Upload image to Supabase Storage ----
  const uploadImage = async (file: File, folder: string): Promise<string | null> => {
    try {
      const ext = file.name.split(".").pop() || "png";
      const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(2)}.${ext}`;

      const { error } = await supabase.storage
        .from("gift-images")
        .upload(fileName, file, { upsert: true, contentType: file.type });

      if (error) {
        console.error("Upload error:", error);
        return null;
      }

      const { data: urlData } = supabase.storage.from("gift-images").getPublicUrl(fileName);
      return urlData.publicUrl;
    } catch {
      return null;
    }
  };

  // ---- Form handlers ----
  const openCreateForm = () => {
    setEditingItem(null);
    setFormName("");
    setFormDesc("");
    setFormPoints("");
    setFormStock("");
    setFormCategory("일반");
    setFormImageFile(null);
    setFormImagePreview(null);
    setShowForm(true);
    setMessage(null);
  };

  const openEditForm = (item: GiftItem) => {
    setEditingItem(item);
    setFormName(item.name);
    setFormDesc(item.description || "");
    setFormPoints(String(item.points_required));
    setFormStock(String(item.stock));
    setFormCategory(item.category);
    setFormImageFile(null);
    setFormImagePreview(item.image_url);
    setShowForm(true);
    setMessage(null);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formPoints || !formStock) {
      setMessage({ type: "error", text: "상품명, 필요 포인트, 재고를 입력해주세요" });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      let imageUrl = editingItem?.image_url || null;

      if (formImageFile) {
        const uploadedUrl = await uploadImage(formImageFile, "items");
        if (uploadedUrl) imageUrl = uploadedUrl;
      }

      const payload = {
        name: formName.trim(),
        description: formDesc.trim(),
        points_required: parseInt(formPoints, 10),
        stock: parseInt(formStock, 10),
        category: formCategory,
        image_url: imageUrl,
      };

      if (editingItem) {
        const { error } = await supabase
          .from("gift_items")
          .update(payload)
          .eq("id", editingItem.id);
        if (error) throw error;
        setMessage({ type: "success", text: "상품이 수정되었습니다" });
      } else {
        const { error } = await supabase
          .from("gift_items")
          .insert(payload);
        if (error) throw error;
        setMessage({ type: "success", text: "새 상품이 등록되었습니다" });
      }

      setShowForm(false);
      await fetchGiftItems();
    } catch {
      setMessage({ type: "error", text: "저장 중 오류가 발생했습니다" });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (item: GiftItem) => {
    try {
      const { error } = await supabase
        .from("gift_items")
        .update({ is_active: !item.is_active })
        .eq("id", item.id);
      if (error) throw error;
      await fetchGiftItems();
    } catch {
      setMessage({ type: "error", text: "상태 변경 중 오류가 발생했습니다" });
    }
  };

  const deleteItem = async (item: GiftItem) => {
    if (!window.confirm(`"${item.name}" 상품을 정말 삭제하시겠습니까?`)) return;
    try {
      const { error } = await supabase.from("gift_items").delete().eq("id", item.id);
      if (error) throw error;
      setMessage({ type: "success", text: "상품이 삭제되었습니다" });
      await fetchGiftItems();
    } catch {
      setMessage({ type: "error", text: "삭제 중 오류가 발생했습니다" });
    }
  };

  // ---- Exchange handlers ----
  const openUploadModal = (exchange: GiftExchange) => {
    setUploadModal(exchange);
    setUploadFile(null);
    setUploadPreview(exchange.gift_image_url);
    setMessage(null);
  };

  const handleUploadGiftImage = async () => {
    if (!uploadModal || !uploadFile) {
      setMessage({ type: "error", text: "이미지를 선택해주세요" });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const uploadedUrl = await uploadImage(uploadFile, "exchanges");
      if (!uploadedUrl) {
        setMessage({ type: "error", text: "이미지 업로드에 실패했습니다. Storage 버킷을 확인해주세요." });
        setUploading(false);
        return;
      }

      const { error } = await supabase
        .from("gift_exchanges")
        .update({
          gift_image_url: uploadedUrl,
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", uploadModal.id);

      if (error) throw error;

      setMessage({ type: "success", text: "기프티콘 이미지가 등록되고 교환이 완료 처리되었습니다" });
      setUploadModal(null);
      await fetchExchanges();
    } catch {
      setMessage({ type: "error", text: "처리 중 오류가 발생했습니다" });
    } finally {
      setUploading(false);
    }
  };

  const cancelExchange = async (exchange: GiftExchange) => {
    if (!window.confirm(`"${exchange.gift_item_name}" 교환을 취소하시겠습니까? 포인트가 환불됩니다.`)) return;

    try {
      // Update exchange status
      const { error: exError } = await supabase
        .from("gift_exchanges")
        .update({ status: "cancelled", completed_at: new Date().toISOString() })
        .eq("id", exchange.id);

      if (exError) throw exError;

      // Refund points
      const { data: profile } = await supabase
        .from("profiles")
        .select("points")
        .eq("id", exchange.user_id)
        .maybeSingle();

      const currentPoints = profile?.points ?? 0;

      await supabase
        .from("profiles")
        .update({ points: currentPoints + exchange.points_spent })
        .eq("id", exchange.user_id);

      // Record refund transaction
      await supabase.from("point_transactions").insert({
        user_id: exchange.user_id,
        type: "적립",
        detail: `${exchange.gift_item_name} 교환 취소 환불`,
        amount: exchange.points_spent,
      });

      setMessage({ type: "success", text: "교환이 취소되고 포인트가 환불되었습니다" });
      await fetchExchanges();
    } catch {
      setMessage({ type: "error", text: "취소 처리 중 오류가 발생했습니다" });
    }
  };

  // ---- Format ----
  const formatDate = (d: string) => new Date(d).toLocaleString("ko-KR", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });

  const statusBadge = (status: string) => {
    switch (status) {
      case "completed": return { bg: "bg-accent-100", text: "text-accent-700", label: "완료" };
      case "cancelled": return { bg: "bg-red-100", text: "text-red-600", label: "취소" };
      default: return { bg: "bg-yellow-100", text: "text-yellow-700", label: "대기중" };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-foreground-800">기프티콘 교환 관리</h2>
          <p className="text-xs text-foreground-600 mt-0.5">기프티콘 상품 등록 및 회원 교환 요청을 관리합니다</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-background-200 rounded-full p-1 w-fit">
        <button
          onClick={() => { setTabMode("items"); setMessage(null); }}
          className={`px-4 py-2 text-xs font-bold rounded-full transition-colors whitespace-nowrap cursor-pointer ${
            tabMode === "items" ? "bg-background-100 text-foreground-800 shadow-sm" : "text-foreground-600 hover:text-foreground-700"
          }`}
        >
          <i className="ri-gift-line mr-1" />
          상품 관리
        </button>
        <button
          onClick={() => { setTabMode("exchanges"); setMessage(null); }}
          className={`px-4 py-2 text-xs font-bold rounded-full transition-colors whitespace-nowrap cursor-pointer ${
            tabMode === "exchanges" ? "bg-background-100 text-foreground-800 shadow-sm" : "text-foreground-600 hover:text-foreground-700"
          }`}
        >
          <i className="ri-exchange-line mr-1" />
          교환 요청
          {exchanges.filter((e) => e.status === "pending").length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-red-500 text-foreground-50 text-[10px] rounded-full">
              {exchanges.filter((e) => e.status === "pending").length}
            </span>
          )}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`px-4 py-3 rounded-lg text-xs font-medium ${
          message.type === "success"
            ? "bg-accent-100/80 text-accent-800 border border-accent-300/60"
            : "bg-red-500/10 text-red-400 border border-red-500/30"
        }`}>
          {message.text}
        </div>
      )}

      {/* ===== 상품 관리 ===== */}
      {tabMode === "items" && (
        <div className="space-y-4">
          {/* Add button */}
          <div className="flex justify-end">
            <button
              onClick={openCreateForm}
              className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line" />
              새 상품 등록
            </button>
          </div>

          {/* Create/Edit Form */}
          {showForm && (
            <div className="bg-background-100 rounded-xl border border-background-300 p-5">
              <h3 className="text-sm font-bold text-foreground-800 mb-4">
                {editingItem ? "상품 수정" : "새 상품 등록"}
              </h3>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">상품명</label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                      placeholder="예: 스타벅스 아메리카노 Tall"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">카테고리</label>
                    <select
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                      className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                    >
                      {["커피/음료", "편의점", "배달/푸드", "문화", "OTT/구독", "일반"].map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">필요 포인트</label>
                    <input
                      type="number"
                      value={formPoints}
                      onChange={(e) => setFormPoints(e.target.value)}
                      className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">재고</label>
                    <input
                      type="number"
                      value={formStock}
                      onChange={(e) => setFormStock(e.target.value)}
                      className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">설명</label>
                  <textarea
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                    className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors resize-none h-20"
                    placeholder="상품에 대한 간단한 설명"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">상품 이미지</label>
                  <div className="flex items-center gap-4">
                    {formImagePreview && (
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={formImagePreview} alt="미리보기" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-background-200 border border-background-300 rounded-lg text-xs text-foreground-600 hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-image-add-line" />
                      이미지 선택
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          setFormImageFile(file);
                          if (file) setFormImagePreview(URL.createObjectURL(file));
                        }}
                      />
                    </label>
                    {formImageFile && <span className="text-[10px] text-foreground-600 truncate">{formImageFile.name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 text-xs text-foreground-600 hover:text-foreground-800 bg-background-200 rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 text-xs font-bold bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? "저장 중..." : editingItem ? "수정하기" : "등록하기"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Gift Items Table */}
          {giftItems.length === 0 ? (
            <div className="bg-background-100 rounded-xl border border-background-300 p-10 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center mb-3">
                <i className="ri-gift-line text-2xl text-foreground-600" />
              </div>
              <p className="text-sm font-medium text-foreground-700 mb-1">등록된 상품이 없습니다</p>
              <p className="text-xs text-foreground-600">새 상품 등록 버튼을 눌러 기프티콘 상품을 추가하세요</p>
            </div>
          ) : (
            <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-300 bg-background-200/50">
                      <th className="text-left py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">이미지</th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">상품명</th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">카테고리</th>
                      <th className="text-right py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">포인트</th>
                      <th className="text-right py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">재고</th>
                      <th className="text-center py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">상태</th>
                      <th className="text-right py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {giftItems.map((item) => (
                      <tr key={item.id} className="border-b border-background-300/50 hover:bg-background-200/30 transition-colors">
                        <td className="py-3 px-4">
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-200">
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-foreground-600">
                                <i className="ri-gift-line" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-xs font-medium text-foreground-700">{item.name}</p>
                          {item.description && <p className="text-[10px] text-foreground-600 mt-0.5 line-clamp-1">{item.description}</p>}
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 text-[10px] bg-background-200 text-foreground-600 rounded whitespace-nowrap">{item.category}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-xs font-bold text-primary-500">{item.points_required.toLocaleString()}P</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`text-xs font-medium ${item.stock <= 5 ? "text-red-400 font-bold" : "text-foreground-600"}`}>{item.stock}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => toggleActive(item)}
                            className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                              item.is_active ? "bg-accent-100 text-accent-700" : "bg-red-100 text-red-600"
                            }`}
                          >
                            {item.is_active ? "판매중" : "숨김"}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => openEditForm(item)}
                              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-300 transition-colors cursor-pointer"
                            >
                              <i className="ri-pencil-line text-xs text-foreground-600" />
                            </button>
                            <button
                              onClick={() => deleteItem(item)}
                              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-100 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-xs text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== 교환 요청 관리 ===== */}
      {tabMode === "exchanges" && (
        <div>
          {exchanges.length === 0 ? (
            <div className="bg-background-100 rounded-xl border border-background-300 p-10 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center mb-3">
                <i className="ri-exchange-line text-2xl text-foreground-600" />
              </div>
              <p className="text-sm font-medium text-foreground-700 mb-1">교환 요청이 없습니다</p>
              <p className="text-xs text-foreground-600">회원이 기프티콘을 교환하면 여기에 표시됩니다</p>
            </div>
          ) : (
            <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-300 bg-background-200/50">
                      <th className="text-left py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">회원</th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">상품명</th>
                      <th className="text-right py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">포인트</th>
                      <th className="text-center py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">상태</th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">신청일</th>
                      <th className="text-right py-3 px-4 text-xs font-bold text-foreground-600 whitespace-nowrap">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exchanges.map((exchange) => {
                      const badge = statusBadge(exchange.status);
                      return (
                        <tr key={exchange.id} className="border-b border-background-300/50 hover:bg-background-200/30 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                                <i className="ri-user-fill text-primary-400 text-xs" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-foreground-700 truncate">{exchange.user_nickname}</p>
                                <p className="text-[10px] text-foreground-600 truncate">{exchange.user_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-xs font-medium text-foreground-700">{exchange.gift_item_name}</p>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="text-xs font-bold text-secondary-500">{exchange.points_spent.toLocaleString()}P</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full ${badge.bg} ${badge.text}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-[10px] text-foreground-600 whitespace-nowrap">{formatDate(exchange.created_at)}</span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center gap-1 justify-end">
                              {exchange.status === "pending" ? (
                                <>
                                  <button
                                    onClick={() => openUploadModal(exchange)}
                                    className="px-2.5 py-1.5 text-[10px] font-bold bg-primary-500 text-foreground-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                                  >
                                    <i className="ri-upload-line mr-1" />
                                    이미지 등록
                                  </button>
                                  <button
                                    onClick={() => cancelExchange(exchange)}
                                    className="px-2 py-1.5 text-[10px] font-medium text-red-400 hover:bg-red-50 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                                  >
                                    취소
                                  </button>
                                </>
                              ) : exchange.status === "completed" && exchange.gift_image_url ? (
                                <a
                                  href={exchange.gift_image_url}
                                  target="_blank"
                                  rel="nofollow noopener noreferrer"
                                  className="px-2.5 py-1.5 text-[10px] font-bold bg-accent-100 text-accent-700 rounded-md hover:bg-accent-200 transition-colors whitespace-nowrap"
                                >
                                  <i className="ri-eye-line mr-1" />
                                  이미지 보기
                                </a>
                              ) : (
                                <span className="text-[10px] text-foreground-600">-</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Upload Modal */}
          {uploadModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/50" onClick={() => setUploadModal(null)} />
              <div className="relative bg-background-100 rounded-xl border border-background-300 shadow-xl w-full max-w-md overflow-hidden z-10">
                <div className="px-5 py-4 border-b border-background-300 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground-800">기프티콘 이미지 등록</h3>
                  <button onClick={() => setUploadModal(null)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-200 transition-colors cursor-pointer">
                    <i className="ri-close-line text-foreground-600" />
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="bg-background-200 rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-foreground-600">회원</span>
                      <span className="text-xs font-medium text-foreground-700">{uploadModal.user_nickname} ({uploadModal.user_email})</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-foreground-600">상품</span>
                      <span className="text-xs font-medium text-foreground-700">{uploadModal.gift_item_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-foreground-600">사용 포인트</span>
                      <span className="text-xs font-bold text-secondary-500">{uploadModal.points_spent.toLocaleString()}P</span>
                    </div>
                  </div>

                  {uploadPreview && (
                    <div className="w-full aspect-[3/2] rounded-lg overflow-hidden bg-background-200">
                      <img src={uploadPreview} alt="미리보기" className="w-full h-full object-contain" />
                    </div>
                  )}

                  <label className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-background-300 rounded-lg hover:border-primary-400 transition-colors cursor-pointer">
                    <i className="ri-image-add-line text-2xl text-foreground-600" />
                    <span className="text-xs text-foreground-600">기프티콘 이미지를 선택하세요</span>
                    <span className="text-[10px] text-foreground-600">PNG, JPG 권장</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setUploadFile(file);
                        if (file) setUploadPreview(URL.createObjectURL(file));
                      }}
                    />
                  </label>

                  <button
                    onClick={handleUploadGiftImage}
                    disabled={uploading || !uploadFile}
                    className="w-full py-2.5 text-sm font-bold bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {uploading ? "업로드 중..." : "등록 및 교환 완료 처리"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Storage 안내 */}
      <div className="mt-6 bg-background-100 rounded-xl border border-accent-200/40 p-5">
        <h2 className="text-sm font-bold text-accent-700 mb-2 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-checkbox-circle-line" />
          </span>
          Storage 설정 완료
        </h2>
        <p className="text-xs text-foreground-600 leading-relaxed">
          <strong className="text-foreground-700">gift-images</strong> 버킷의 RLS 정책이 모두 구성되었습니다.
          관리자만 이미지를 업로드/수정/삭제할 수 있으며, 업로드된 이미지는 누구나 조회 가능합니다.
        </p>
      </div>
    </div>
  );
}